import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Radio, RefreshCw, Coins, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, formatUSD, formatPct, formatTs, formatNum, TIMEFRAMES } from './ops_helpers';

const signalTone = (s) => {
  const v = (s || '').toUpperCase();
  if (v === 'BUY' || v === 'LONG') return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
  if (v === 'SELL' || v === 'SHORT') return 'bg-rose-500/20 text-rose-300 border-rose-500/30';
  return 'bg-zinc-700/40 text-zinc-400 border-zinc-700';
};
const actionTone = (a) => {
  const v = (a || '').toLowerCase();
  if (v === 'open') return 'bg-violet-500/20 text-violet-300 border-violet-500/30';
  if (v === 'blocked') return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
  if (v === 'hold') return 'bg-sky-500/20 text-sky-300 border-sky-500/30';
  return 'bg-zinc-700/40 text-zinc-400 border-zinc-700';
};

const Kpi = ({ label, value, tone = 'text-zinc-100', hint, testId }) => (
  <div data-testid={testId} className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`mt-1 text-lg font-bold ${tone}`}>{value}</p>
    {hint && <p className="text-[10px] text-zinc-500 mt-0.5">{hint}</p>}
  </div>
);

// Tabla de desglose por dimensión (confidence, score, reason, hora, sesión)
const BreakdownTable = ({ title, icon: Icon, rows, keyLabel = 'Grupo', testId }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold text-zinc-200 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-violet-400" />} {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800">
              <TableHead className="text-zinc-500 text-xs">{keyLabel}</TableHead>
              <TableHead className="text-zinc-500 text-xs text-right">Trades</TableHead>
              <TableHead className="text-zinc-500 text-xs text-right">WR</TableHead>
              <TableHead className="text-zinc-500 text-xs text-right">Neto</TableHead>
              <TableHead className="text-zinc-500 text-xs text-right">Comis.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r, i) => (
              <TableRow key={i} className="border-zinc-800 text-xs">
                <TableCell className="font-mono text-zinc-300">{r.key}</TableCell>
                <TableCell className="text-right text-zinc-300">{r.trades}</TableCell>
                <TableCell className="text-right text-zinc-400">{formatPct(r.win_rate, 0)}</TableCell>
                <TableCell className={`text-right font-semibold ${r.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {r.net >= 0 ? '+' : ''}{formatUSD(r.net)}
                </TableCell>
                <TableCell className="text-right text-amber-400/80">{formatUSD(r.fees)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export const BotSignals = ({ botId }) => {
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        apiGet(`/api/ops/bots/${botId}/signal-analytics?days=${days}`),
        apiGet(`/api/ops/bots/${botId}/signals?days=${days}&limit=120`),
      ]);
      setAnalytics(a);
      setSignals(s.signals || []);
    } catch (e) {
      toast.error(`Error señales ${botId}: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [botId, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sm = analytics?.summary;
  const hasTrades = sm && sm.trades > 0;
  const hasSignals = signals.length > 0;
  // Detecta tipo de bot por los campos presentes en las señales
  const isSentiment = signals.some((x) => x.confidence != null || x.score != null);

  return (
    <div className="space-y-4" data-testid={`bot-signals-${botId}`}>
      {/* Controles */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Radio className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-semibold text-zinc-200">Lab de datos · Señales y decisiones</span>
        </div>
        <div className="flex items-center gap-1">
          {TIMEFRAMES.map((tf) => (
            <Button
              key={tf.id}
              data-testid={`signals-tf-${botId}-${tf.id}`}
              size="sm"
              variant={days === tf.days ? 'default' : 'outline'}
              className={days === tf.days ? 'bg-violet-600 hover:bg-violet-700 h-7 px-2 text-xs' : 'border-zinc-800 h-7 px-2 text-xs'}
              onClick={() => setDays(tf.days)}
            >
              {tf.label}
            </Button>
          ))}
          <Button
            data-testid={`signals-refresh-${botId}`}
            size="sm" variant="outline"
            className="border-zinc-800 h-7 px-2"
            onClick={fetchData} disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPIs bruto vs neto */}
      {hasTrades ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <Kpi testId={`signals-kpi-net-${botId}`} label="Neto" value={formatUSD(sm.net_profit)}
              tone={sm.net_profit >= 0 ? 'text-emerald-400' : 'text-rose-400'} hint="bruto − comisiones" />
            <Kpi label="Bruto (precio)" value={formatUSD(sm.gross_profit - sm.gross_loss + sm.commissions)}
              tone="text-zinc-300" hint="solo precio" />
            <Kpi testId={`signals-kpi-fees-${botId}`} label="Comisiones" value={formatUSD(sm.commissions)}
              tone="text-amber-400" hint={`${formatUSD(sm.avg_commission_per_trade, 2)}/trade`} />
            <Kpi label="Win rate" value={formatPct(sm.win_rate, 1)} tone="text-zinc-100"
              hint={`${sm.wins}W / ${sm.losses}L`} />
            <Kpi label="Profit factor" value={formatNum(sm.profit_factor, 2)}
              tone={sm.profit_factor >= 1 ? 'text-emerald-400' : 'text-rose-400'} />
            <Kpi label="Expectancy" value={formatUSD(sm.expectancy)}
              tone={sm.expectancy >= 0 ? 'text-emerald-400' : 'text-rose-400'} hint="por trade" />
            <Kpi label="Racha pérdidas" value={sm.max_loss_streak}
              tone={sm.max_loss_streak >= 3 ? 'text-rose-400' : 'text-zinc-100'} hint={`máx ganada ${sm.max_win_streak}`} />
          </div>

          {/* Aviso de comisiones (el caso Sentimental) */}
          {sm.commissions > 0 && (sm.net_profit) < (sm.net_profit + sm.commissions) && (
            <Card className="bg-amber-950/20 border-amber-900/40">
              <CardContent className="p-3 flex items-center gap-2 text-xs text-amber-200">
                <Coins className="w-4 h-4 text-amber-400 shrink-0" />
                <span>
                  Las comisiones se comieron <strong>{formatUSD(sm.commissions)}</strong> del resultado.
                  Sin comisiones el neto sería <strong>{formatUSD(sm.net_profit + sm.commissions)}</strong>.
                </span>
              </CardContent>
            </Card>
          )}

          {/* Desgloses */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <BreakdownTable testId={`bd-direction-${botId}`} title="Long vs Short" icon={TrendingUp}
              rows={analytics.by_direction} keyLabel="Dirección" />
            {isSentiment ? (
              <>
                <BreakdownTable testId={`bd-confidence-${botId}`} title="Por confidence" icon={Filter}
                  rows={analytics.by_confidence} keyLabel="Confidence" />
                <BreakdownTable testId={`bd-score-${botId}`} title="Por score" icon={Filter}
                  rows={analytics.by_score} keyLabel="Score" />
              </>
            ) : (
              <BreakdownTable testId={`bd-session-${botId}`} title="Por sesión" icon={Filter}
                rows={analytics.by_session} keyLabel="Sesión" />
            )}
            <BreakdownTable testId={`bd-reason-${botId}`} title="Por motivo (API/setup)" icon={TrendingDown}
              rows={analytics.by_reason} keyLabel="Motivo" />
            <BreakdownTable testId={`bd-hour-${botId}`} title="Por hora (UTC)" icon={TrendingUp}
              rows={analytics.by_hour} keyLabel="Hora" />
          </div>
        </>
      ) : (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardContent className="p-6 text-center text-zinc-500 text-sm">
            Aún no hay trades cerrados en esta ventana. La analítica bruto/neto aparecerá cuando el bot opere.
          </CardContent>
        </Card>
      )}

      {/* Tabla de señales / decisiones */}
      <Card className="bg-zinc-900/60 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-zinc-200">Registro de decisiones</CardTitle>
          <CardDescription className="text-xs">
            Cada señal del bot: entre o no, queda el motivo. {signals.length} registros.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {hasSignals ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-zinc-500 text-xs">Hora</TableHead>
                    <TableHead className="text-zinc-500 text-xs">Señal</TableHead>
                    <TableHead className="text-zinc-500 text-xs">Acción</TableHead>
                    <TableHead className="text-zinc-500 text-xs">Motivo</TableHead>
                    {isSentiment ? (
                      <>
                        <TableHead className="text-zinc-500 text-xs text-right">Conf.</TableHead>
                        <TableHead className="text-zinc-500 text-xs text-right">Score</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-zinc-500 text-xs text-right">ADX</TableHead>
                        <TableHead className="text-zinc-500 text-xs text-right">ATR</TableHead>
                      </>
                    )}
                    <TableHead className="text-zinc-500 text-xs text-right">Spread</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signals.map((s, i) => (
                    <TableRow key={i} className="border-zinc-800 text-xs hover:bg-zinc-800/30">
                      <TableCell className="text-zinc-400 whitespace-nowrap">{formatTs(s.ts)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${signalTone(s.signal)}`}>{s.signal}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${actionTone(s.action)}`}>{s.action}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-zinc-400 max-w-[220px] truncate">
                        {s.decision_reason || s.reason || '—'}
                      </TableCell>
                      {isSentiment ? (
                        <>
                          <TableCell className="text-right text-zinc-300">{s.confidence ?? '—'}</TableCell>
                          <TableCell className={`text-right ${(s.score || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {s.score != null ? formatNum(s.score, 0) : '—'}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right text-zinc-300">{s.adx != null ? formatNum(s.adx, 1) : '—'}</TableCell>
                          <TableCell className="text-right text-zinc-300">{s.atr != null ? formatNum(s.atr, 1) : '—'}</TableCell>
                        </>
                      )}
                      <TableCell className="text-right text-zinc-400">{s.spread != null ? formatNum(s.spread, 0) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-center text-zinc-500 py-6 text-sm">
              Sin señales todavía. El EA las enviará en cada vela / refresco de la API.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BotSignals;
